import React, { useState } from "react";
import { Box, Typography, Button, Paper } from "@mui/material";
import { CareerPlanPDFPreviewer } from "@/components/pdf/PDFPreviewer";

// Mock data for the Career Plan PDF
const mockCareerPlanData = {
  user: {
    name: "John Doe",
    studentId: "STU-12345",
  },
  pathway: {
    name: "Software Developer",
    cluster: "Information Technology",
    description: "Design, develop, and maintain software applications.",
    sentiment: "I love coding and building new things with technology!",
  },
  hat: {
    displayName: "Problem Solver",
    msTextOverview:
      "Software developers solve complex problems by creating innovative solutions.",
    description:
      "Developers create applications that help people accomplish tasks.",
  },
  topHat: {
    description:
      "Developers create applications that help people accomplish tasks.",
  },
  buildingBlocks: [
    {
      buildingBlockId: "1",
    },
    {
      buildingBlockId: "2",
    },
    {
      buildingBlockId: "3",
    },
  ],
  cmsBuildingBlocks: [
    { id: "1", name: "Critical Thinking" },
    { id: "2", name: "Communication" },
    { id: "3", name: "Collaboration" },
  ],
  buildingBlocksActivities: [
    {
      details: {
        category: {
          name: "Critical Thinking",
        },
      },
      userActivityDescription:
        "Analyze code to identify bugs and improve performance.",
      sentiments: [
        {
          reason:
            "I enjoyed debugging because it challenged me to think systematically.",
        },
      ],
    },
    {
      details: {
        category: {
          name: "Communication",
        },
      },
      userActivityDescription:
        "Explain technical concepts to non-technical team members.",
      sentiments: [
        {
          reason: "Teaching others helps me understand the material better.",
        },
      ],
    },
    {
      details: {
        category: {
          name: "Collaboration",
        },
      },
      userActivityDescription:
        "Work with a team to build a mobile app for a local business.",
      sentiments: [
        {
          reason:
            "Working in a team taught me how to share responsibilities and communicate effectively.",
        },
      ],
    },
  ],
  educationOptions: [
    { title: "4-Year Bachelor Degree" },
    { title: "2-Year Associate Degree" },
    { title: "Certificate Program" },
    { title: "Bootcamp" },
  ],
  recommendedCourses: [
    { body: "Introduction to Computer Science" },
    { body: "Data Structures and Algorithms" },
    { body: "Web Development" },
    { body: "Mobile App Development" },
    { body: "Software Engineering Principles" },
  ],
  nextSteps: [
    {
      buildingBlock: {
        name: "Programming Fundamentals",
      },
      body: "Learn the basics of programming through online courses.",
    },
    {
      buildingBlock: {
        name: "Build Your Portfolio",
      },
      body: "Create personal projects to showcase your skills.",
    },
    {
      buildingBlock: {
        name: "Attend Hackathons",
      },
      body: "Join local coding competitions to practice and network.",
    },
    {
      buildingBlock: {
        name: "Find a Mentor",
      },
      body: "Connect with experienced developers in your community.",
    },
    {
      buildingBlock: {
        name: "Apply for Internships",
      },
      body: "Gain real-world experience through summer internships.",
    },
    {
      buildingBlock: {
        name: "Develop Soft Skills",
      },
      body: "Enhance your communication and teamwork abilities.",
    },
  ],
};

export const PDFPreviewPage: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Career Plan PDF Preview
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="body1" paragraph>
          Click the button below to preview the Career Plan PDF. You can view it
          in the browser without downloading the file.
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          This is useful for developing and testing the PDF layout without
          generating the actual PDF file.
        </Typography>

        <Button
          variant="contained"
          onClick={() => setIsVisible(!isVisible)}
          sx={{ mt: 2 }}
        >
          {isVisible ? "Hide Preview" : "Show PDF Preview"}
        </Button>
      </Paper>

      {isVisible && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Live PDF Preview
          </Typography>
          <Box
            sx={{
              width: "100%",
              height: "calc(100vh - 300px)",
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              overflow: "auto",
            }}
          >
            <CareerPlanPDFPreviewer
              careerPlanData={mockCareerPlanData}
              width="100%"
              height="100%"
              showToolbar={true}
            />
          </Box>
        </Paper>
      )}
    </Box>
  );
};
